'use client';

import type { SetStateAction, Dispatch, ReactElement } from 'react';
import { createContext, useContext, useState, useEffect } from 'react';
import type { FILTER_GROUP, FilterGroup, FilterOption, SelectedFilters } from '@/types/filter';
import {
    countFilters,
    getFilteredGamesCount
} from '@/app/(pages)/(category)/c/[slug]/components/category-filter/utils';
import { INITIAL_SELECTED_FILTERS } from '@/utils/constants';
import type { Game } from '@/types/games';
import '@/assets/styles/common.globals.scss';

export type CategoryFilterContext = {
    appliedFiltersCount: number;
    showPopUp: boolean;
    canFilter: boolean;
    selectedGroup: FilterGroup | null;
    selectedFilters: SelectedFilters;
    setSelectedGroup: Dispatch<SetStateAction<FilterGroup | null>>;
    selectFilter: (filter: FilterOption) => void;
    removeFilter: (group: FILTER_GROUP, filter: FilterOption) => void;
    handleBack: () => void;
    clearFilters: () => void;
    applyFilters: () => void;
    openPopUp: (anchorElement?: HTMLElement | null) => void;
    closePopUp: () => void;
    filtersGroups: FilterGroup[];
    filteredGamesCount: number;
    clearDisabled: boolean | null;
    filtersSelected: boolean;
    categoryName: string;
};

type Props = {
    filtersGroups: FilterGroup[];
    appliedFilters: SelectedFilters;
    setAppliedFiltersAction: Dispatch<SetStateAction<SelectedFilters>>;
    categoryName: string;
    canFilter: boolean;
    games: Game[];
    children: ReactElement;
};

export const CategoryFilterContext = createContext<CategoryFilterContext>({} as CategoryFilterContext);

export const CategoryFilterProvider = ({
    filtersGroups,
    appliedFilters,
    setAppliedFiltersAction,
    categoryName,
    canFilter,
    games,
    children
}: Props) => {
    const [showPopUp, setShowPopUp] = useState(false);
    const [selectedGroup, setSelectedGroup] = useState<FilterGroup | null>(null);
    const [selectedFilters, setSelectedFilters] = useState<SelectedFilters>(appliedFilters);

    const selectFilter = (filter: FilterOption) => {
        const group = selectedGroup?.key;
        if (!group) {
            return;
        }

        setSelectedFilters((prevState: any) => ({
            ...prevState,
            [group]: [...prevState[group], filter]
        }));
        //userTrackingPush(getUTFiltersSelected(categoryName, group, filter.title));
    };

    const removeFilter = (group: FILTER_GROUP, filter: FilterOption) => {
        const current: FilterOption[] = selectedFilters[group];
        setSelectedFilters((prevState: any) => ({
            ...prevState,
            [group]: current.filter((v) => v.title !== filter.title)
        }));
        //userTrackingPush(getUTFiltersRemoved(categoryName, group, filter.title));
    };

    const filtersSelected = Object.values(selectedFilters).some((f: any) => f.length);

    const filteredGamesCount = getFilteredGamesCount(games, selectedFilters);

    const POPUP_DESKTOP_HEIGHT = 650;
    const SMOOTH_SCROLL_DURATION_MS = 300;

    const lockBodyAndShowPopup = () => {
        document.body.style.top = `-${window.scrollY}px`;
        document.body.classList.add('no-scroll');
        //userTrackingPush(getUTFiltersOpened(categoryName));
        setShowPopUp(true);
    };

    const openPopUp = (anchorElement?: HTMLElement | null) => {
        const fitsInViewport =
            !anchorElement || window.innerHeight - anchorElement.getBoundingClientRect().bottom >= POPUP_DESKTOP_HEIGHT;

        if (fitsInViewport) {
            lockBodyAndShowPopup();
            return;
        }

        anchorElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
        setTimeout(lockBodyAndShowPopup, SMOOTH_SCROLL_DURATION_MS);
    };

    const closePopUp = (clear = true) => {
        const lockedScrollY = Math.abs(parseInt(document.body.style.top || '0', 10));
        document.body.classList.remove('no-scroll');
        document.body.style.top = '';
        window.scrollTo({ top: lockedScrollY, behavior: 'instant' });
        setShowPopUp(false);
        setSelectedGroup(null);
        if (clear) {
            // Rollback to initial state
            setSelectedFilters(appliedFilters);
        }
    };
    const handleBack = () => setSelectedGroup(null);

    const clearDisabled = !filtersSelected || (selectedGroup && !selectedFilters[selectedGroup.key]?.length);

    const clearFilters = () => {
        const group = selectedGroup?.key;
        if (group) {
            setSelectedFilters((prevState: any) => ({ ...prevState, [group]: [] }));
            //userTrackingPush(getUTFiltersCleared(categoryName, group));
        } else {
            setSelectedFilters(INITIAL_SELECTED_FILTERS);
            //userTrackingPush(getUTFiltersAllCleared(categoryName));
        }
    };

    const appliedFiltersCount = countFilters(appliedFilters);

    const applyFilters = () => {
        setAppliedFiltersAction(selectedFilters);
        //userTrackingPush(getUTFiltersApplied(categoryName, selectedFilters));
        closePopUp(false);
    };

    useEffect(() => {
        // Clear all when category changed
        closePopUp();
        clearFilters();
    }, [categoryName]);

    return (
        <CategoryFilterContext.Provider
            value={{
                appliedFiltersCount,
                openPopUp,
                showPopUp,
                selectedGroup,
                setSelectedGroup,
                selectedFilters,
                handleBack,
                clearFilters,
                applyFilters,
                closePopUp,
                canFilter,
                filtersGroups,
                selectFilter,
                removeFilter,
                filteredGamesCount,
                clearDisabled,
                filtersSelected,
                categoryName
            }}>
            {children}
        </CategoryFilterContext.Provider>
    );
};

export const useCategoryFilterContext = () => useContext(CategoryFilterContext);
